import { useState, useRef, useCallback } from 'react';
import { TranscriptSegment } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const WS_URL = API_URL.replace(/^http/, 'ws');

interface UseWebSocketReturn {
  isConnected: boolean;
  segments: TranscriptSegment[];
  connect: (visitId: string, lang?: string) => Promise<void>;
  disconnect: () => void;
  sendAudio: (blob: Blob) => void;
  clearSegments: () => void;
  setSegments: React.Dispatch<React.SetStateAction<TranscriptSegment[]>>;
}

export function useWebSocket(): UseWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const wsRef = useRef<WebSocket | null>(null);

  const connect = useCallback((visitId: string, lang: string = 'en'): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(`${WS_URL}/ws/transcript/${visitId}?lang=${lang}`);

      ws.onopen = () => {
        setIsConnected(true);
        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const segment: TranscriptSegment = JSON.parse(event.data);
          setSegments((prev) => {
            if (segment.isFinal) {
              const withoutInterim = prev.filter((s) => s.isFinal);
              return [...withoutInterim, segment];
            }
            const finals = prev.filter((s) => s.isFinal);
            return [...finals, segment];
          });
        } catch {
          // Ignore non-JSON messages
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
      };

      ws.onerror = () => {
        setIsConnected(false);
        reject(new Error('WebSocket connection failed'));
      };

      wsRef.current = ws;
    });
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const sendAudio = useCallback((blob: Blob) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      blob.arrayBuffer().then((buffer) => {
        wsRef.current?.send(buffer);
      });
    }
  }, []);

  const clearSegments = useCallback(() => {
    setSegments([]);
  }, []);

  return { isConnected, segments, connect, disconnect, sendAudio, clearSegments, setSegments };
}
